/**
 * PublicBuzzPage — /p/:slug (V3a, "our Linktree"). PUBLIC, no chrome.
 * Title + bio + big link buttons; a small lead form at the bottom captures
 * the visitor as a Customer (attribution channel 'buzzpage', UTM params
 * passed through from the page URL). Manus-minimal, monochrome.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { BrandLogo } from '@/components/brand/BrandLogo';

const API_URL = (import.meta.env.VITE_API_URL as string | undefined) || 'https://api.buzzbitx.com';

interface PublicPage {
  title: string;
  bio: string | null;
  links: Array<{ label: string; url: string }>;
  /** V38 — the merchant's look. Absent on an older server = monochrome. */
  brand?: { name: string; logoUrl: string | null; primaryColor: string | null };
}

/** Same second check as the booking page: only a hex literal reaches `style`. */
const HEX = /^#(?:[0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/;
const HTTPS = /^https:\/\//i;

/** Dark or white text, whichever reads on a 6-digit hex (3-digit stays white). */
function inkOn(hex: string): string {
  if (hex.length !== 7) return '#ffffff';
  const n = parseInt(hex.slice(1), 16);
  return 0.299 * ((n >> 16) & 255) + 0.587 * ((n >> 8) & 255) + 0.114 * (n & 255) > 160 ? '#111827' : '#ffffff';
}

export default function PublicBuzzPage(): React.ReactElement {
  const { slug } = useParams<{ slug: string }>();
  const [search] = useSearchParams();
  const [page, setPage] = useState<PublicPage | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [website, setWebsite] = useState(''); // honeypot
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${API_URL}/api/buzz-page/public/${slug}`);
        const json = await res.json();
        if (!res.ok) throw new Error(json?.error?.message ?? 'This page is unavailable.');
        setPage((json?.data ?? json) as PublicPage);
      } catch (e) {
        setLoadError(e instanceof Error ? e.message : 'This page is unavailable.');
      }
    })();
  }, [slug]);

  const submit = useCallback(async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setSubmitError(null);
    try {
      const res = await fetch(`${API_URL}/api/buzz-page/public/${slug}/lead`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email,
          name: name || undefined,
          website,
          utm_source: search.get('utm_source') ?? undefined,
          utm_medium: search.get('utm_medium') ?? undefined,
          utm_campaign: search.get('utm_campaign') ?? undefined,
          // Which ad / Reel / keyword: without these the lead only said "instagram".
          utm_content: search.get('utm_content') ?? undefined,
          utm_term: search.get('utm_term') ?? undefined,
          utm_id: search.get('utm_id') ?? undefined,
          ad_id: search.get('ad_id') ?? undefined,
          fbclid: search.get('fbclid') ?? undefined,
          landing_url: window.location.href.slice(0, 1000),
          referrer: document.referrer ? document.referrer.slice(0, 1000) : undefined,
        }),
      });
      if (!res.ok) {
        const json = await res.json();
        throw new Error(json?.error?.message ?? 'Could not submit. Please try again.');
      }
      setDone(true);
    } catch (err) {
      setSubmitError(err instanceof Error ? err.message : 'Could not submit. Please try again.');
    } finally {
      setSubmitting(false);
    }
  }, [slug, email, name, website, search]);

  const accent = page?.brand?.primaryColor && HEX.test(page.brand.primaryColor) ? page.brand.primaryColor : null;

  return (
    <div className="min-h-screen bg-gray-50 px-4 py-12 pb-cookie-banner [--pb-cookie-base:3rem]">
      <div className="mx-auto w-full max-w-md">
        {loadError ? (
          <p className="text-center text-sm text-gray-500">{loadError}</p>
        ) : !page ? (
          <p className="text-center text-sm text-gray-400">Loading…</p>
        ) : (
          <>
            <div className="text-center">
              {page.brand?.logoUrl && HTTPS.test(page.brand.logoUrl) && (
                <img
                  src={page.brand.logoUrl}
                  alt=""
                  className="mx-auto mb-4 h-14 w-auto object-contain"
                  data-testid="buzzpage-logo"
                />
              )}
              <h1 className="text-2xl font-bold text-gray-900">{page.title}</h1>
              {page.bio && <p className="mt-2 text-sm text-gray-500 whitespace-pre-line">{page.bio}</p>}
            </div>

            <div className="mt-8 space-y-3">
              {page.links.map((l) => (
                <a
                  key={`${l.label}-${l.url}`}
                  href={l.url}
                  {...(accent !== null ? { style: { backgroundColor: accent, borderColor: accent, color: inkOn(accent) } } : {})}
                  className="block w-full rounded-full border border-gray-900 bg-gray-900 px-6 py-3 text-center text-sm font-semibold text-white transition-colors hover:bg-gray-700"
                >
                  {l.label}
                </a>
              ))}
              {page.links.length === 0 && (
                <p className="text-center text-sm text-gray-400">No links yet.</p>
              )}
            </div>

            <div className="mt-10 rounded-2xl border border-gray-200 bg-white p-6">
              {done ? (
                <p className="text-center text-sm font-medium text-gray-900">Thanks — you're on the list ✓</p>
              ) : (
                <form onSubmit={submit}>
                  <p className="text-sm font-semibold text-gray-900">Stay in touch</p>
                  <div className="mt-3 space-y-2">
                    <input
                      type="text"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      placeholder="Your name"
                      className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-gray-400 focus:outline-none"
                    />
                    <input
                      type="email"
                      required
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="you@example.com"
                      className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-gray-400 focus:outline-none"
                    />
                    {/* Honeypot — hidden from humans, tempting to bots */}
                    <input
                      type="text"
                      value={website}
                      onChange={(e) => setWebsite(e.target.value)}
                      tabIndex={-1}
                      autoComplete="off"
                      className="hidden"
                      aria-hidden="true"
                    />
                  </div>
                  {submitError && <p className="mt-2 text-xs text-red-600">{submitError}</p>}
                  <button
                    type="submit"
                    data-cookie-avoid
                    disabled={submitting || !email}
                    className="mt-3 w-full rounded-full bg-gray-900 px-5 py-2.5 text-sm font-semibold text-white hover:bg-gray-700 disabled:opacity-50"
                  >
                    {submitting ? 'Sending…' : 'Subscribe'}
                  </button>
                </form>
              )}
            </div>

            <div className="mt-8 flex justify-center">
              <BrandLogo size="sm" />
            </div>
          </>
        )}
      </div>
    </div>
  );
}
